import { createSlice } from "@reduxjs/toolkit"
import { createAppAsyncThunk } from "app/hooks"
import { IFoodState, ISearchFoodOverlayDisplaySection } from "./foodSlice.types"
import { convertServerToClientFood } from "./foodConverter"
import { ISearchFoodsPayload } from "./foodSlice.types"
import foodAPI from "./foodAPI"
import { resetStoreAction, DEFAULT_RECENT_INGREDIENTS_PAGE_SIZE } from "config"
import { IEdamaFood } from "model/food/types/edama.types"
import {
  ICreateMyFoodPayload,
  IUpdateMyFoodPayload,
} from "model/food/types/foodPayload.types"
import { CreateRecentIngredientPayload } from "model/food/types/recentIngredient.types"
import { v4 as uuidv4 } from "uuid"

export const deleteFood = createAppAsyncThunk(
  "food/deleteFood",
  async (id: string, { rejectWithValue }) => {
    try {
      await foodAPI.deleteFood(id)
      return id
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const updateFood = createAppAsyncThunk(
  "food/updateFood",
  async (payload: IUpdateMyFoodPayload, { rejectWithValue }) => {
    try {
      const food = await foodAPI.updateFood(payload)
      return food
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const createFood = createAppAsyncThunk(
  "food/createFood",
  async (payload: ICreateMyFoodPayload, { rejectWithValue }) => {
    try {
      const food = await foodAPI.createFood(payload)
      return food
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

let gettingSuggestedTextController: AbortController | null = null

export const getSuggestedSearchTexts = createAppAsyncThunk(
  "food/getSuggestedSearchTexts",
  async (keyword: string, { rejectWithValue }) => {
    if (gettingSuggestedTextController) {
      gettingSuggestedTextController.abort()
    }
    if (!keyword) {
      return []
    }

    const localController = new AbortController()
    gettingSuggestedTextController = localController

    try {
      const results = await foodAPI.getSuggestedSearchTexts(
        keyword,
        localController.signal,
      )
      return results
    } catch (err: any) {
      if (localController.signal.aborted) {
        return rejectWithValue({ aborted: true })
      }
      return rejectWithValue(err)
    } finally {
      if (gettingSuggestedTextController === localController) {
        gettingSuggestedTextController = null
      }
    }
  },
)

let gettingExternalSuggestedTextController: AbortController | null = null

export const getExternalSuggestedSearchTexts = createAppAsyncThunk(
  "food/getExternalSuggestedSearchTexts",
  async (keyword: string, { rejectWithValue }) => {
    if (gettingExternalSuggestedTextController) {
      gettingExternalSuggestedTextController.abort()
    }
    if (!keyword) {
      return []
    }

    const localController = new AbortController()
    gettingExternalSuggestedTextController = localController

    try {
      const results = await foodAPI.getSuggestedSearchTexts(
        keyword,
        localController.signal,
      )
      return results
    } catch (err: any) {
      if (localController.signal.aborted) {
        return rejectWithValue({ aborted: true })
      }
      return rejectWithValue(err)
    } finally {
      if (gettingExternalSuggestedTextController === localController) {
        gettingExternalSuggestedTextController = null
      }
    }
  },
)

export const searchFoods = createAppAsyncThunk(
  "food/searchFoods",
  async (payload: ISearchFoodsPayload, { rejectWithValue }) => {
    try {
      const { foods, next } = await foodAPI.searchFoods({
        ...payload,
        sources: ["EDAMAM", "MY_FOOD", "OPEN_FOOD_FACTS"],
      })

      return {
        foods,
        session: next,
      }
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const searchEdamaFoods = createAppAsyncThunk(
  "food/searchEdamaFoods",
  async (payload: ISearchFoodsPayload, { rejectWithValue }) => {
    try {
      const { foods, next } = await foodAPI.searchFoods({
        ...payload,
        sources: ["EDAMAM"],
      })

      return {
        foods: foods as IEdamaFood[],
        session: next,
      }
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const getMyFoods = createAppAsyncThunk(
  "food/getMyFoods",
  async (_, { rejectWithValue }) => {
    try {
      const foods = await foodAPI.getMyFoods()
      return foods
    } catch (err: any) {
      console.error(err)
      return rejectWithValue(err)
    }
  },
)

export const getFoodDetails = createAppAsyncThunk(
  "food/getFoodDetails",
  async (foodId: string, { rejectWithValue }) => {
    try {
      const food = await foodAPI.getFood(foodId)
      return convertServerToClientFood(food)
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

// Recent Ingredients
let getRecentIngredientsAbortController: AbortController | null = null

export const getRecentIngredients = createAppAsyncThunk(
  "food/getRecentIngredients",
  async ({ page }: { page: number }, { rejectWithValue }) => {
    const isFirstPage = page === 1
    if (getRecentIngredientsAbortController && isFirstPage) {
      getRecentIngredientsAbortController.abort()
    }

    const localController = new AbortController()
    getRecentIngredientsAbortController = localController

    try {
      const response = await foodAPI.getRecentIngredients(
        { page, perPage: DEFAULT_RECENT_INGREDIENTS_PAGE_SIZE },
        localController.signal,
      )
      return response
    } catch (err: any) {
      if (localController.signal.aborted) {
        return rejectWithValue({ aborted: true })
      }
      return rejectWithValue(err)
    } finally {
      if (getRecentIngredientsAbortController === localController) {
        getRecentIngredientsAbortController = null
      }
    }
  },
)

export const createRecentIngredient = createAppAsyncThunk(
  "food/createRecentIngredient",
  async (payload: CreateRecentIngredientPayload, { rejectWithValue }) => {
    try {
      const response = await foodAPI.createRecentIngredient(payload)
      return response
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const deleteRecentIngredient = createAppAsyncThunk(
  "food/deleteRecentIngredient",
  async (id: string, { rejectWithValue }) => {
    try {
      await foodAPI.deleteRecentIngredient(id)
      return id
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

const initialState: IFoodState = {
  isSearchInAddMealPageVisible: false,
  searchFoodOverlayDisplaySection:
    ISearchFoodOverlayDisplaySection.AUTOCOMPLETE,

  deleteFoodLoading: false,
  deleteFoodFailed: undefined,
  deleteFoodSuccess: undefined,

  updateFoodLoading: false,
  updateFoodSuccess: undefined,
  updateFoodFailed: undefined,

  foodDetails: undefined,
  getFoodDetailsLoading: false,
  getFoodDetailsFailed: undefined,
  detailsBackPath: undefined,

  createFoodLoading: false,
  createFoodFailed: undefined,
  createFoodSuccess: undefined,

  myFoods: [],
  getMyFoodsLoading: false,
  getMyFoodsFailed: undefined,

  keyword: "",
  getSuggestedSearchTextsLoading: false,
  getSuggestedSearchTextsFailed: undefined,
  suggestedSearchTexts: [],
  isNoSuggestedSearchTexts: false,

  searchSession: "",
  searchFoodsResults: [],
  searchFoodsLoading: false,
  searchFoodsFailed: undefined,
  noSearchedFoodFounded: false,

  externalKeyword: "",
  getExternalSuggestedSearchTextsLoading: false,
  getExternalSuggestedSearchTextsFailed: undefined,
  externalSuggestedSearchTexts: [],
  isNoExternalSuggestedSearchTexts: false,

  externalSearchSession: "",
  searchEdamaFoodsResults: [],
  searchEdamaFoodsLoading: false,
  searchEdamaFoodsFailed: undefined,
  noSearchedExternalFoodFounded: false,

  // Recent Ingredients
  recentIngredients: [],
  getRecentIngredientsLoading: false,
  getRecentIngredientsFailed: undefined,
  isNoRecentIngredients: false,
  recentIngredientsPage: 0,
  hasMoreRecentIngredients: true,

  createRecentIngredientLoading: false,
  createRecentIngredientSuccess: undefined,
  createRecentIngredientFailed: undefined,

  deleteRecentIngredientLoading: false,
  deleteRecentIngredientSuccess: undefined,
  deleteRecentIngredientFailed: undefined,
}

export const foodSlice = createSlice({
  name: "food",
  initialState,
  reducers: {
    setSearchFoodOverlayDisplaySection(state, { payload }) {
      state.searchFoodOverlayDisplaySection = payload
    },

    setSearchInAddMealPageVisible(state, { payload }) {
      state.isSearchInAddMealPageVisible = payload
    },

    resetExternalFoodsState(state) {
      state.searchEdamaFoodsResults = []
      state.externalKeyword = ""
      state.externalSuggestedSearchTexts = []
    },

    resetSearchedFoodsState(state) {
      state.isSearchInAddMealPageVisible = false
      state.searchFoodsResults = []
      state.keyword = ""
      state.suggestedSearchTexts = []
    },

    setFoodDetails(state, { payload }) {
      state.foodDetails = payload
    },

    setDetailsBackPath(state, { payload }) {
      state.detailsBackPath = payload
    },

    resetSearchedFoods(state) {
      state.searchFoodsResults = []
    },

    resetSearchedExternalFoods(state) {
      state.searchEdamaFoodsResults = []
    },

    onExternalKeywordChange(state, { payload }) {
      state.externalKeyword = payload
      state.searchEdamaFoodsResults = []

      if (payload === "") {
        state.isNoExternalSuggestedSearchTexts = false
      }
    },

    onKeywordChange(state, { payload }) {
      state.keyword = payload
      state.searchFoodsResults = []

      if (payload === "") {
        state.isNoSuggestedSearchTexts = false
      }
    },

    resetFoodState() {
      return initialState
    },

    resetRecentIngredients(state) {
      state.recentIngredients = []
      state.getRecentIngredientsLoading = false
      state.getRecentIngredientsFailed = undefined
      state.isNoRecentIngredients = false
      state.recentIngredientsPage = 0
      state.hasMoreRecentIngredients = true
    },
  },
  extraReducers(builder) {
    builder
      .addCase(deleteFood.pending, (state) => {
        state.deleteFoodLoading = true
      })
      .addCase(deleteFood.fulfilled, (state, { payload }) => {
        state.deleteFoodLoading = false
        state.deleteFoodSuccess = payload
      })
      .addCase(deleteFood.rejected, (state, { payload }) => {
        state.deleteFoodLoading = false
        state.deleteFoodFailed = payload
      })

      .addCase(getFoodDetails.pending, (state) => {
        state.getFoodDetailsLoading = true
      })
      .addCase(getFoodDetails.fulfilled, (state, { payload }) => {
        state.getFoodDetailsLoading = false
        state.foodDetails = payload
      })
      .addCase(getFoodDetails.rejected, (state, { payload }) => {
        state.getFoodDetailsLoading = false
        state.getFoodDetailsFailed = payload
      })

      .addCase(createFood.pending, (state) => {
        state.createFoodLoading = true
      })
      .addCase(createFood.fulfilled, (state, { payload }) => {
        state.createFoodLoading = false
        state.createFoodSuccess = payload
      })
      .addCase(createFood.rejected, (state, { payload }) => {
        state.createFoodLoading = false
        state.createFoodFailed = payload
      })

      .addCase(updateFood.pending, (state) => {
        state.updateFoodLoading = true
      })
      .addCase(updateFood.fulfilled, (state, { payload }) => {
        state.updateFoodLoading = false
        state.updateFoodSuccess = payload
      })
      .addCase(updateFood.rejected, (state, { payload }) => {
        state.updateFoodLoading = false
        state.updateFoodFailed = payload
      })

      .addCase(getMyFoods.pending, (state) => {
        state.getMyFoodsLoading = true
      })

      .addCase(getMyFoods.fulfilled, (state, { payload }) => {
        state.getMyFoodsLoading = false
        state.myFoods = payload
      })

      .addCase(getMyFoods.rejected, (state, { payload }) => {
        state.getMyFoodsLoading = false
        state.getMyFoodsFailed = payload
        state.myFoods = []
      })

      .addCase(getSuggestedSearchTexts.pending, (state) => {
        state.getSuggestedSearchTextsLoading = !!state.keyword
      })
      .addCase(getSuggestedSearchTexts.fulfilled, (state, { payload }) => {
        state.getSuggestedSearchTextsLoading = false

        if (payload) {
          state.suggestedSearchTexts = payload
          state.isNoSuggestedSearchTexts =
            payload.length === 0 && !!state.keyword
        }
      })

      .addCase(getSuggestedSearchTexts.rejected, (state, { payload }) => {
        if ((payload as any)?.aborted) return
        state.getSuggestedSearchTextsLoading = false
        state.getSuggestedSearchTextsFailed = payload
      })

      .addCase(getExternalSuggestedSearchTexts.pending, (state) => {
        state.getExternalSuggestedSearchTextsLoading = !!state.keyword
      })
      .addCase(
        getExternalSuggestedSearchTexts.fulfilled,
        (state, { payload }) => {
          state.getExternalSuggestedSearchTextsLoading = false

          if (payload) {
            state.externalSuggestedSearchTexts = payload
            state.isNoExternalSuggestedSearchTexts = payload.length === 0
          }
        },
      )

      .addCase(
        getExternalSuggestedSearchTexts.rejected,
        (state, { payload }) => {
          if ((payload as any)?.aborted) return
          state.getExternalSuggestedSearchTextsLoading = false
          state.getExternalSuggestedSearchTextsFailed = payload
        },
      )

      .addCase(searchFoods.pending, (state, { meta }) => {
        state.searchFoodsLoading = true
        state.keyword = meta.arg.keyword
        state.searchSession = meta.arg.session ?? ""
      })
      .addCase(searchFoods.fulfilled, (state, { payload }) => {
        state.searchFoodsLoading = false

        const newFoods = payload.foods.map((food) => ({
          ...food,
          uuid: uuidv4(),
        }))

        state.searchFoodsResults = [...state.searchFoodsResults, ...newFoods]
        state.searchSession = payload.session
        state.noSearchedFoodFounded = state.searchFoodsResults.length === 0
      })

      .addCase(searchFoods.rejected, (state, { payload }) => {
        state.searchFoodsLoading = false
        state.searchFoodsFailed = payload
        state.searchFoodsResults = []
      })

      .addCase(searchEdamaFoods.pending, (state, { meta }) => {
        state.searchEdamaFoodsLoading = true
        state.externalKeyword = meta.arg.keyword
        state.externalSearchSession = meta.arg.session ?? ""
      })
      .addCase(searchEdamaFoods.fulfilled, (state, { payload }) => {
        state.searchEdamaFoodsLoading = false

        const newFoods = payload.foods.map((food) => ({
          ...food,
          uuid: uuidv4(),
        }))

        state.searchEdamaFoodsResults = [
          ...state.searchEdamaFoodsResults,
          ...newFoods,
        ]

        state.externalSearchSession = payload.session
        state.noSearchedExternalFoodFounded =
          state.searchEdamaFoodsResults.length === 0
      })

      .addCase(searchEdamaFoods.rejected, (state, { payload }) => {
        state.searchEdamaFoodsLoading = false
        state.searchEdamaFoodsFailed = payload
        state.searchEdamaFoodsResults = []
      })

      // Recent Ingredients
      .addCase(getRecentIngredients.pending, (state, { meta }) => {
        state.getRecentIngredientsLoading = true
        if (meta.arg.page === 1) {
          state.recentIngredients = []
        }
      })
      .addCase(getRecentIngredients.fulfilled, (state, { payload }) => {
        state.getRecentIngredientsLoading = false
        if (payload) {
          state.recentIngredients = [
            ...state.recentIngredients,
            ...payload.recentIngredients,
          ]
          state.recentIngredientsPage = payload.page
          state.hasMoreRecentIngredients = payload.page < payload.totalPages
          state.isNoRecentIngredients =
            payload.page === 1 && payload.recentIngredients.length === 0
        }
      })
      .addCase(getRecentIngredients.rejected, (state, { payload }) => {
        if ((payload as any)?.aborted) return
        state.getRecentIngredientsLoading = false
        state.getRecentIngredientsFailed = payload
      })

      .addCase(createRecentIngredient.pending, (state) => {
        state.createRecentIngredientLoading = true
      })
      .addCase(createRecentIngredient.fulfilled, (state, { payload }) => {
        state.createRecentIngredientLoading = false
        state.createRecentIngredientSuccess = payload
        // Fire and forget - no list updates
        // Recent ingredients will be refreshed on next page load
      })
      .addCase(createRecentIngredient.rejected, (state, { payload }) => {
        state.createRecentIngredientLoading = false
        state.createRecentIngredientFailed = payload
      })

      // Optimistic delete
      .addCase(deleteRecentIngredient.pending, (state, { meta: { arg } }) => {
        state.deleteRecentIngredientLoading = true
        state.recentIngredients = state.recentIngredients.filter(
          (i) => i.id !== arg,
        )
      })
      .addCase(deleteRecentIngredient.fulfilled, (state, { payload }) => {
        state.deleteRecentIngredientLoading = false
        state.deleteRecentIngredientSuccess = payload
        if (state.recentIngredients.length === 0) {
          state.isNoRecentIngredients = true
        }
      })
      .addCase(deleteRecentIngredient.rejected, (state, { payload }) => {
        state.deleteRecentIngredientLoading = false
        state.deleteRecentIngredientFailed = payload
      })

      .addCase(resetStoreAction, () => {
        return initialState
      })
  },
})

export const {
  onKeywordChange,
  resetSearchedFoods,
  setFoodDetails,
  setDetailsBackPath,
  resetSearchedFoodsState,
  onExternalKeywordChange,
  resetSearchedExternalFoods,
  setSearchInAddMealPageVisible,
  setSearchFoodOverlayDisplaySection,
  resetRecentIngredients,
} = foodSlice.actions

export default foodSlice.reducer
